import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowsPointingOutIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  EyeIcon,
  GlobeAltIcon,
  ListBulletIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  ClockIcon,
  RocketLaunchIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import DashboardLayout from "../../components/DashboardLayout";
import PremiumModelPicker from "../../components/common/PremiumModelPicker";
import StatusBadge from "../../components/common/StatusBadge";
import SummaryText from "../../components/common/SummaryText";
import VoiceInputButton from "../../components/common/VoiceInputButton";
import WeaveLoader from "../../components/common/WeaveLoader";
import { useToast } from "../../contexts/ToastContext";
import { setCurrentProject } from "../../redux/project/projectSlice";
import { changeRequestsService } from "../../services/changeRequests";
import { deploymentsService } from "../../services/deployments";
import { environmentsService } from "../../services/environments";
import { projectsService } from "../../services/projects";
import { changeTotals } from "../../utils/agentLog";
import { getApiErrorMessage } from "../../utils/axios";
import { handleBillingError } from "../../utils/billing";
import { clearChangeDraft, persistChangeDraft, useChangeDraft } from "../../utils/changeDraft";
import { refreshSidebarBadges } from "../../utils/sidebarBadges";
import { SITE_BUILD_STEPS, siteBuildStepIndex } from "../../utils/siteBuildProgress";
import { sitePath } from "../../utils/urls";
import { ChangeCard, stoppedChange, rejectedChange } from "./Changes";

const DEVICE_VIEWS = [
  { id: "desktop", label: "Desktop", width: null, Icon: ComputerDesktopIcon },
  { id: "tablet", label: "iPad", width: 834, Icon: DeviceTabletIcon },
  { id: "mobile", label: "Mobile", width: 390, Icon: DevicePhoneMobileIcon },
];

const ACTIVE_CHANGE_STATUSES = new Set(["queued", "running"]);
const TOAST_MS = 5000;

const changesHaveDraftWork = (changeRequests) => (
  (changeRequests || []).some((change) => (
    ACTIVE_CHANGE_STATUSES.has(change.status)
    || change.status === "completed"
    || change.status === "failed"
    || change.status === "canceled"
  ))
);

const Preview = ({ currentUser }) => {
  const showBuildCost = Boolean(currentUser?.features?.build_cost);
  const project = useSelector((state) => state.project.currentProject);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { success, error, warning } = useToast();
  const toastError = useCallback((message, duration, options) => (
    error(message, duration === undefined ? TOAST_MS : duration, options)
  ), [error]);
  const logChangeHash = location.pathname.match(/\/preview\/changes\/([^/]+)\/?$/)?.[1] || null;
  const [detailChange, setDetailChange] = useState(null);
  const chatListRef = useRef(null);
  const envParam = searchParams.get("env");
  const runParam = searchParams.get("run");
  const pathParam = searchParams.get("path");
  const [previewData, setPreviewData] = useState(null);
  // Live sites open on Live; draft until the site is live or when draft work exists.
  const [viewMode, setViewMode] = useState(project?.status === "live" ? "live" : "draft");
  const [deviceView, setDeviceView] = useState("desktop");
  const [message, setMessage] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeModelOverride, setComposeModelOverride] = useState("");
  const [sending, setSending] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const publishBaselineRef = useRef([]);
  const publishDeploymentHashRef = useRef(null);
  const [draftPanelOpen, setDraftPanelOpen] = useState(false);
  const [checkpointUrl, setCheckpointUrl] = useState(null);
  // Path inside the iframe (pathname + search). Survives draft/live/checkpoint swaps; resets per site.
  const [previewPath, setPreviewPath] = useState("/");
  const previewPathRef = useRef("/");
  // Last few in-site paths for "back to preview" after an external breakout.
  const [recentPaths, setRecentPaths] = useState(["/"]);
  const [externalBreakout, setExternalBreakout] = useState(null);
  const [iframeSrc, setIframeSrc] = useState("");
  const [iframeGen, setIframeGen] = useState(0);
  const iframeRef = useRef(null);
  const [previewMissing, setPreviewMissing] = useState(false);
  const [restoringPreview, setRestoringPreview] = useState(false);
  const [showUpdating, setShowUpdating] = useState(false);
  const [updatingVisible, setUpdatingVisible] = useState(false);
  const draftPreviewRevisionRef = useRef(null);
  const projectHashRef = useRef(project?.hash);
  projectHashRef.current = project?.hash;
  const envParamRef = useRef(envParam);
  const runParamRef = useRef(runParam);
  envParamRef.current = envParam;
  runParamRef.current = runParam;
  // First preview payload for this site; used to pick Live/Draft before the iframe mounts.
  const previewBootstrappedRef = useRef(false);

  const loadPreviewData = useCallback(() => {
    if (!project?.hash) return;
    projectsService.getProjectPreview(project.hash, (response) => {
      if (project.hash !== projectHashRef.current) return;
      const data = response.data;
      setPreviewData(data);
      // Resolve the tab with the payload so we don't briefly load Live ("Site not found")
      // before effects catch up. Only on first payload for this site.
      if (!previewBootstrappedRef.current) {
        const env = envParamRef.current;
        if (env === "live" || env === "draft") {
          setViewMode(env);
        } else {
          const draftWork = changesHaveDraftWork(data?.environment?.change_requests);
          setViewMode(
            runParamRef.current || project.status !== "live" || draftWork ? "draft" : "live",
          );
        }
      }
      previewBootstrappedRef.current = true;
    });
  }, [project?.hash, project?.status]);

  const patchPreviewChange = (hash, patch) => {
    setPreviewData((current) => {
      if (!current?.environment) return current;
      return {
        ...current,
        environment: {
          ...current.environment,
          change_requests: (current.environment.change_requests || []).map((item) => (
            item.hash === hash ? { ...item, ...patch } : item
          )),
        },
      };
    });
  };

  // Same route component across sites; drop checkpoint/draft state so the new site opens at base.
  useEffect(() => {
    setPreviewData(null);
    previewBootstrappedRef.current = false;
    setCheckpointUrl(null);
    setDetailChange(null);
    setViewMode(project?.status === "live" ? "live" : "draft");
    setMessage("");
    setComposeOpen(false);
    setComposeModelOverride("");
  }, [project?.hash]);

  // After project-switch clear above, restore any saved draft for this site.
  useChangeDraft(project?.hash, message, setMessage);

  const siteIsBuilding = project?.status !== "live";
  const environment = previewData?.environment || null;
  const changeRequests = environment?.change_requests || [];
  // Preview already embeds all CRs; sitebuilder is just the initial-build one.
  const buildChange = changeRequests.find((change) => change.agent_type === "sitebuilder") || null;
  const hasRunning = useMemo(
    () => changeRequests.some((change) => ACTIVE_CHANGE_STATUSES.has(change.status)),
    [changeRequests],
  );
  const hasDraftWork = useMemo(
    () => changesHaveDraftWork(changeRequests),
    [changeRequests],
  );

  const latestDeployment = environment?.latest_deployment || null;
  const envPublishing = latestDeployment?.status === "building"
    || (
      environment?.status === "processing"
      && environment?.status_message === "Publishing to live"
    );

  // One API: preview (CRs + draft/live URLs). Fetch once on open; poll while agents run or a publish is in flight.
  // Submit/resolve call loadPreviewData again, which restarts the poll if the new state is active.
  useEffect(() => {
    if (!project?.hash) return undefined;
    loadPreviewData();
    if (!hasRunning && !publishing && !envPublishing) return undefined;
    const timer = setInterval(loadPreviewData, 5000);
    return () => clearInterval(timer);
  }, [loadPreviewData, project?.hash, hasRunning, publishing, envPublishing]);

  // Preview payload omits project.status / build_progress. Refresh while building.
  useEffect(() => {
    if (!project?.hash || !siteIsBuilding) return undefined;
    const refresh = () => {
      projectsService.getProject(project.hash, (response) => {
        if (project.hash !== projectHashRef.current) return;
        dispatch(setCurrentProject(response.data));
      });
    };
    refresh();
    if (!hasRunning) return undefined;
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, [project?.hash, siteIsBuilding, hasRunning, dispatch]);

  const openChangeLogs = useCallback((changeHash) => {
    if (!project || !changeHash) return;
    navigate({
      pathname: sitePath(project, `preview/changes/${changeHash}/`),
      search: location.search,
    });
  }, [navigate, project, location.search]);

  const closeChangeLogs = useCallback(() => {
    if (!project) return;
    setDetailChange(null);
    navigate({
      pathname: sitePath(project, "preview/"),
      search: location.search,
    });
  }, [navigate, project, location.search]);

  // Stable boolean so polling detail does not restart the interval on every response.
  const logChange = (detailChange?.hash === logChangeHash ? detailChange : null)
    || changeRequests.find((item) => item.hash === logChangeHash);
  const logChangeActive = Boolean(
    logChangeHash && logChange && (
      ACTIVE_CHANGE_STATUSES.has(logChange.status)
      || (logChange.agent_runs || []).some((run) => run.status === "running" || run.status === "pending")
    ),
  );

  const modalChange = detailChange?.hash === logChangeHash
    ? detailChange
    : (logChange || (logChangeHash ? { hash: logChangeHash, agent_runs: [] } : null));

  const modalActions = useMemo(() => {
    if (!modalChange) {
      return { canReject: false, canDiscard: false, canRetry: false };
    }
    const changeKey = (change) => [new Date(change.create_date).getTime(), change.id || 0];
    const isNewer = (other, change) => {
      const [aTime, aId] = changeKey(other);
      const [bTime, bId] = changeKey(change);
      return aTime > bTime || (aTime === bTime && aId > bId);
    };
    const openDraft = changeRequests.filter(
      (change) => change.status !== "rejected" && change.status !== "published",
    );
    const tip = openDraft[0] || null;
    const canActPaused = openDraft
      .filter((change) => isNewer(change, modalChange))
      .every((change) => (
        change.status === "queued"
        && !(change.agent_runs || []).some((run) => run.status === "running" || run.status === "pending")
      ));
    const paused = modalChange.status === "failed" || modalChange.status === "canceled";
    const isSitebuilder = modalChange.agent_type === "sitebuilder";
    return {
      canReject: !isSitebuilder && tip?.hash === modalChange.hash && modalChange.status === "completed",
      canDiscard: !isSitebuilder && canActPaused && paused,
      canRetry: canActPaused && paused,
    };
  }, [modalChange, changeRequests]);

  // Detail endpoint (with agent_log) while the change modal is open; poll only if that change is active.
  useEffect(() => {
    if (!project?.hash || !logChangeHash) {
      setDetailChange(null);
      return undefined;
    }
    let cancelled = false;
    const loadDetail = () => {
      changeRequestsService.get(project.hash, logChangeHash, (response) => {
        if (cancelled || project.hash !== projectHashRef.current) return;
        setDetailChange(response.data);
      });
    };
    loadDetail();
    if (!logChangeActive) return () => { cancelled = true; };
    const timer = setInterval(loadDetail, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [project?.hash, logChangeHash, logChangeActive]);

  useEffect(() => {
    if (envParam === "live" || envParam === "draft") {
      setViewMode(envParam);
    }
  }, [envParam]);

  // No ?env= / ?run=: live sites open on Live, or Draft when there is draft work.
  useEffect(() => {
    if (envParam || runParam || !previewData) return;
    if (project?.status !== "live") {
      setViewMode("draft");
      return;
    }
    setViewMode(hasDraftWork ? "draft" : "live");
  }, [previewData, envParam, runParam, project?.status, hasDraftWork]);

  useEffect(() => {
    if (!runParam || !changeRequests.length) return;
    for (const change of changeRequests) {
      const run = (change.agent_runs || []).find((item) => item.hash === runParam);
      if (run?.preview_url) {
        setCheckpointUrl((current) => keepPreviewUrl(current, run.preview_url));
        setViewMode("draft");
        return;
      }
    }
    const change = changeRequests.find((item) => item.hash === runParam);
    const latestRun = change?.agent_runs?.slice(-1)[0];
    if (latestRun?.preview_url) {
      setCheckpointUrl((current) => keepPreviewUrl(current, latestRun.preview_url));
      setViewMode("draft");
    }
  }, [runParam, changeRequests]);

  const lastCheckpointUrl = latestRunPreviewUrl(changeRequests);
  const projectLiveUrl = project?.custom_domain
    ? `https://${project.custom_domain}`
    : (project?.production_url || "");
  // Wait for preview payload before any iframe URL. projectLiveUrl alone can 404 ("Site not found")
  // while we still don't know draft vs live.
  const liveUrl = previewData ? (previewData.live_url || projectLiveUrl || "") : "";
  const openDraftUrl = previewData
    ? (previewData.draft_url || environment?.preview_url || lastCheckpointUrl || "")
    : "";
  // Live sites with no draft workspace yet: draft tab can show live until a change lands.
  const draftUrl = openDraftUrl || liveUrl || "";
  const draftIsLiveFallback = Boolean(
    liveUrl
    && !checkpointUrl
    && !hasDraftWork
    && (!openDraftUrl || openDraftUrl === liveUrl)
  );
  let iframeBase = draftUrl;
  if (viewMode === "live") {
    iframeBase = liveUrl;
  } else if (checkpointUrl) {
    iframeBase = checkpointUrl;
  }
  const draftPath = previewUrlPath(draftUrl);
  const livePath = previewUrlPath(liveUrl);
  const checkpointPath = previewUrlPath(checkpointUrl);
  const iframeTargetKey = viewMode === "live"
    ? livePath
    : (checkpointPath || draftPath);
  // Origin+path only so signed draft query churn from polling does not reload the iframe.
  let iframeBaseKey = "";
  if (iframeBase) {
    try {
      const parsed = new URL(iframeBase);
      iframeBaseKey = `${parsed.origin}${parsed.pathname}`;
    } catch {
      iframeBaseKey = iframeBase;
    }
  }
  const failedSiteBuild = Boolean(
    buildChange?.status === "failed"
    || changeRequests.some((change) => change.agent_type === "sitebuilder" && change.status === "failed"),
  );
  const shareUrl = iframeBase ? joinPreviewUrl(iframeBase, previewPath) : "";
  const displayHost = previewDisplayHost(iframeBase)
    || (projectLiveUrl && previewDisplayHost(projectLiveUrl))
    || (project?.subdomain ? `${project.subdomain}…` : "");
  let displayPath = normalizePreviewPath(previewPath);
  if (iframeBase) {
    try {
      const basePath = new URL(iframeBase).pathname.replace(/\/$/, "") || "";
      if (basePath) {
        displayPath = displayPath === "/" ? `${basePath}/` : `${basePath}${displayPath}`;
      }
    } catch {
      /* keep site path */
    }
  }
  const showPath = displayPath !== "/";
  const canOpenUrl = Boolean(shareUrl || iframeBase);
  const openHref = shareUrl || iframeBase || "";
  const buildStep = siteBuildStepIndex(project, buildChange);
  const device = DEVICE_VIEWS.find((item) => item.id === deviceView) || DEVICE_VIEWS[0];
  // Running change with no ready draft yet: empty state instead of a 404 iframe + restore bar.
  const draftComingSoon = viewMode === "draft" && hasRunning && (previewMissing || !iframeSrc);

  useEffect(() => {
    previewPathRef.current = "/";
    setPreviewPath("/");
    setRecentPaths(["/"]);
    setExternalBreakout(null);
    setIframeSrc("");
    setCheckpointUrl(null);
    draftPreviewRevisionRef.current = null;
    setShowUpdating(false);
  }, [project?.hash]);

  // Deep-link from SEO / elsewhere: /preview/?path=/brands/
  useEffect(() => {
    if (!pathParam) return;
    const next = normalizePreviewPath(pathParam);
    previewPathRef.current = next;
    setPreviewPath(next);
    setRecentPaths((prev) => pushRecentPath(prev, next));
    if (iframeBase) setIframeSrc(joinPreviewUrl(iframeBase, next));
  }, [pathParam, project?.hash, iframeBaseKey]);

  // Rebake on target/mode/site change. Path-only keys miss live↔draft when draft falls back
  // to the live URL; project?.hash covers the reset that clears iframeSrc on site switch.
  useEffect(() => {
    if (!iframeTargetKey || !iframeBase) {
      setIframeSrc("");
      return;
    }
    setIframeSrc(joinPreviewUrl(iframeBase, previewPathRef.current));
  }, [iframeTargetKey, iframeBaseKey, viewMode, project?.hash]);

  useEffect(() => {
    if (!iframeTargetKey || !iframeBase || viewMode === "live") {
      setPreviewMissing(false);
      return undefined;
    }
    if (!iframeTargetKey.includes("/draft/")) {
      setPreviewMissing(false);
      return undefined;
    }
    let cancelled = false;
    fetch(iframeBase, { method: "HEAD", mode: "cors" })
      .then((response) => {
        if (!cancelled) setPreviewMissing(!response.ok);
      })
      .catch(() => {
        if (!cancelled) setPreviewMissing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [iframeTargetKey, viewMode, iframeGen, hasRunning]);

  // New/running change: hide the draft iframe until HEAD proves it exists (avoids 404 + restore UX).
  useEffect(() => {
    if (!hasRunning || viewMode !== "draft") return;
    if (!iframeTargetKey.includes("/draft/")) return;
    setPreviewMissing(true);
  }, [hasRunning, viewMode, iframeTargetKey]);

  useEffect(() => {
    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.source !== "weave-preview" || typeof data.path !== "string") return;
      const allowed = [liveUrl, draftUrl, checkpointUrl]
        .filter(Boolean)
        .map((url) => {
          try {
            return new URL(url).origin;
          } catch {
            return "";
          }
        })
        .filter(Boolean);
      if (!allowed.includes(event.origin)) return;

      const href = typeof data.href === "string" ? data.href : "";
      // Same-origin /draft/… can redirect into the Weave SPA; treat like external, no Stay.
      if (data.appShell || isAppShellHref(href, iframeBase)) {
        setExternalBreakout({ href, allowStay: false });
        setIframeSrc("about:blank");
        return;
      }

      if (data.external) {
        setExternalBreakout({ href, allowStay: true });
        return;
      }

      // Bridge posts the full iframe pathname; keep only the site path so draft/live swaps work.
      let next = normalizePreviewPath(data.path);
      for (const base of [checkpointUrl, draftUrl, liveUrl].filter(Boolean)) {
        try {
          const prefix = new URL(base).pathname.replace(/\/$/, "") || "";
          if (prefix && (next === prefix || next.startsWith(`${prefix}/`))) {
            next = normalizePreviewPath(next.slice(prefix.length) || "/");
            break;
          }
        } catch {
          /* ignore bad base */
        }
      }
      setExternalBreakout(null);
      setRecentPaths((prev) => pushRecentPath(prev, next));
      if (previewPathRef.current === next) return;
      previewPathRef.current = next;
      setPreviewPath(next);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [liveUrl, draftUrl, checkpointUrl, iframeBase]);

  const draftPreviewRevision = useMemo(
    () => draftPreviewRevisionKey(environment, changeRequests),
    [environment, changeRequests],
  );

  useEffect(() => {
    if (viewMode !== "draft" || checkpointUrl || !iframeSrc) return undefined;

    const prev = draftPreviewRevisionRef.current;
    if (prev === null) {
      draftPreviewRevisionRef.current = draftPreviewRevision;
      return undefined;
    }
    if (!hasRunning) {
      draftPreviewRevisionRef.current = draftPreviewRevision;
      return undefined;
    }
    if (prev === draftPreviewRevision) return undefined;

    draftPreviewRevisionRef.current = draftPreviewRevision;
    setIframeSrc(joinPreviewUrl(iframeBase, previewPathRef.current));
    setIframeGen((n) => n + 1);
    setShowUpdating(true);
    return undefined;
  }, [
    draftPreviewRevision,
    hasRunning,
    viewMode,
    checkpointUrl,
    iframeSrc,
    iframeBase,
  ]);

  useEffect(() => {
    if (!showUpdating) {
      setUpdatingVisible(false);
      return undefined;
    }
    const enter = requestAnimationFrame(() => setUpdatingVisible(true));
    const fade = setTimeout(() => setUpdatingVisible(false), 4500);
    const hide = setTimeout(() => setShowUpdating(false), 5000);
    return () => {
      cancelAnimationFrame(enter);
      clearTimeout(fade);
      clearTimeout(hide);
    };
  }, [showUpdating]);
  const unpublishedCompleted = useMemo(
    () => changeRequests.filter((change) => change.status === "completed"),
    [changeRequests],
  );
  const isPublishing = publishing || envPublishing;
  const canPublish = Boolean(
    environment?.hash && unpublishedCompleted.length && !envPublishing,
  );
  // Oldest → newest for chat; API returns newest first.
  const chatChanges = changeRequests
    .filter((change) => change.status !== "rejected")
    .reverse();
  const draftStartedAt = chatChanges.length
    ? (environment?.create_date || chatChanges[0].create_date)
    : null;
  const draftStartedLabel = draftStartedAt
    ? new Date(draftStartedAt).toLocaleString([], {
      weekday: "long",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    : "";

  useEffect(() => {
    const list = chatListRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
  }, [changeRequests.length, viewMode]);

  useEffect(() => {
    if (!publishing) return;
    const trackedHash = publishDeploymentHashRef.current;
    const tracked = trackedHash && latestDeployment?.hash === trackedHash
      ? latestDeployment
      : null;
    if (tracked?.status === "failed" || environment?.status_message === "Publish failed") {
      setPublishing(false);
      publishBaselineRef.current = [];
      publishDeploymentHashRef.current = null;
      error("Publish failed");
      return;
    }
    const baseline = publishBaselineRef.current;
    if (!baseline.length) return;
    const byHash = Object.fromEntries(changeRequests.map((change) => [change.hash, change]));
    const stillPending = baseline.some((hash) => byHash[hash]?.status === "completed");
    if (stillPending || envPublishing || tracked?.status === "building") return;
    const published = baseline.every((hash) => (
      !byHash[hash] || byHash[hash].status === "published"
    ));
    if (!published) return;
    setPublishing(false);
    publishBaselineRef.current = [];
    publishDeploymentHashRef.current = null;
    success("Published live", 3000);
    // Stay on draft if more draft work exists (e.g. a new change queued during publish).
    if (!hasDraftWork) {
      setCheckpointUrl(null);
      setViewMode("live");
      setSearchParams({ env: "live" });
    }
  }, [
    publishing,
    envPublishing,
    latestDeployment,
    changeRequests,
    hasDraftWork,
    environment?.status_message,
    success,
    error,
    setSearchParams,
  ]);

  const copyUrl = async () => {
    if (!canOpenUrl) return;
    await navigator.clipboard.writeText(shareUrl || iframeBase);
    success("Copied", 2000);
  };

  const refreshIframe = () => {
    if (!iframeBase) return;
    setIframeSrc(joinPreviewUrl(iframeBase, previewPathRef.current));
    setIframeGen((n) => n + 1);
  };

  const returnToPreviewPath = (path) => {
    if (!iframeBase) return;
    const next = normalizePreviewPath(path || recentPaths[0] || "/");
    previewPathRef.current = next;
    setPreviewPath(next);
    setRecentPaths((prev) => pushRecentPath(prev, next));
    setExternalBreakout(null);
    setIframeSrc(joinPreviewUrl(iframeBase, next));
    setIframeGen((n) => n + 1);
  };

  const dismissExternalBreakout = () => setExternalBreakout(null);

  // Redirects that skip PreviewBridge (no Navigation API): catch SPA on the draft host.
  const onPreviewIframeLoad = () => {
    if (!iframeBase || !iframeBase.includes("/draft/")) return;
    const frame = iframeRef.current;
    if (!frame) return;
    try {
      const loc = frame.contentWindow.location;
      if (loc.href === "about:blank") return;
      if (isAppShellHref(loc.href, iframeBase)) {
        setExternalBreakout({ href: loc.href, allowStay: false });
        setIframeSrc("about:blank");
      }
    } catch {
      /* cross-origin live preview */
    }
  };

  const selectLive = () => {
    setCheckpointUrl(null);
    setViewMode("live");
    setSearchParams({ env: "live" });
  };

  const selectDraft = () => {
    setCheckpointUrl(null);
    setViewMode("draft");
    setSearchParams({ env: "draft" });
  };

  const viewCheckpoint = (url, runHash) => {
    setCheckpointUrl(url);
    setViewMode("draft");
    setSearchParams(runHash ? { env: "draft", run: runHash } : { env: "draft" });
  };

  const restorePreview = () => {
    if (!project?.hash || !environment?.hash || restoringPreview) return;
    let sha = environment.github_sha || "";
    try {
      const parts = new URL(iframeBase).pathname.split("/").filter(Boolean);
      if (parts[0] === "draft" && /^[0-9a-f]{7,40}$/i.test(parts[2] || "")) {
        sha = parts[2];
      }
    } catch {
      /* use environment.github_sha */
    }
    if (!sha) {
      error("No snapshot to restore");
      return;
    }
    setRestoringPreview(true);
    environmentsService.rebuildPreview(
      project.hash,
      environment.hash,
      sha,
      () => {
        const started = Date.now();
        const timer = setInterval(() => {
          fetch(iframeBase, { method: "HEAD", mode: "cors" })
            .then((response) => {
              if (!response.ok) {
                if (Date.now() - started > 10 * 60 * 1000) {
                  clearInterval(timer);
                  setRestoringPreview(false);
                  error("Preview restore timed out");
                }
                return;
              }
              clearInterval(timer);
              setPreviewMissing(false);
              setRestoringPreview(false);
              refreshIframe();
            })
            .catch(() => {
              if (Date.now() - started > 10 * 60 * 1000) {
                clearInterval(timer);
                setRestoringPreview(false);
                error("Preview restore failed");
              }
            });
        }, 3000);
      },
      (err) => {
        setRestoringPreview(false);
        error(getApiErrorMessage(err, "Could not restore preview"));
      },
    );
  };

  const publish = () => {
    if (!canPublish || publishing) return;
    publishBaselineRef.current = unpublishedCompleted.map((change) => change.hash);
    publishDeploymentHashRef.current = null;
    setPublishing(true);
    environmentsService.publish(
      project.hash,
      environment.hash,
      (response) => {
        publishDeploymentHashRef.current = response?.data?.latest_deployment?.hash || null;
        loadPreviewData();
      },
      (err) => {
        setPublishing(false);
        publishBaselineRef.current = [];
        publishDeploymentHashRef.current = null;
        error(getApiErrorMessage(err, "Publish failed"));
      },
    );
  };

  const resolveChange = (change, action) => {
    if (action === "cancel" && !window.confirm("Cancel this change?")) {
      return;
    }
    const fail = (err) => toastError(getApiErrorMessage(err, "Could not update that change"));
    const apply = (response) => {
      const updated = response?.data;
      if (updated?.hash) {
        setDetailChange(updated);
        patchPreviewChange(updated.hash, updated);
        if (action === "reject") loadPreviewData();
        return;
      }
      loadPreviewData();
    };
    if (action === "retry") changeRequestsService.retry(project.hash, change.hash, apply, fail);
    else if (action === "cancel") {
      if (change.status === "running") {
        patchPreviewChange(change.hash, stoppedChange(change));
      }
      changeRequestsService.cancel(project.hash, change.hash, apply, (err) => {
        loadPreviewData();
        fail(err);
      });
    } else if (action === "approve") {
      publishBaselineRef.current = unpublishedCompleted.map((item) => item.hash);
      publishDeploymentHashRef.current = null;
      setPublishing(true);
      changeRequestsService.approve(
        project.hash,
        change.hash,
        (response) => {
          const building = (response?.data?.deployments || []).find((d) => d.status === "building");
          publishDeploymentHashRef.current = building?.hash
            || response?.data?.deployment_hash
            || null;
          apply(response);
          loadPreviewData();
        },
        (err) => {
          setPublishing(false);
          publishBaselineRef.current = [];
          publishDeploymentHashRef.current = null;
          fail(err);
        },
      );
    } else if (action === "reject") {
      // Leave the undone checkpoint; tip draft_url will move to the restored sha.
      setCheckpointUrl(null);
      patchPreviewChange(change.hash, rejectedChange(change));
      if (logChangeHash) closeChangeLogs();
      changeRequestsService.reject(project.hash, change.hash, apply, (err) => {
        loadPreviewData();
        fail(err);
      });
    }
  };

  const send = (options = {}) => {
    if (!message.trim() || !project?.hash || sending) return;
    const description = message.trim();
    const queueBehind = changeRequests.some(
      (change) => ACTIVE_CHANGE_STATUSES.has(change.status)
        || (change.agent_runs || []).some((run) => run.status === "running" || run.status === "pending"),
    );
    persistChangeDraft(project.hash, description);
    setSending(true);
    changeRequestsService.create(
      project.hash,
      description,
      "chat",
      () => {
        clearChangeDraft(project.hash);
        setMessage("");
        // Swap to draft so the running change shows in the preview pane.
        setCheckpointUrl(null);
        setViewMode("draft");
        setSearchParams({ env: "draft" });
        loadPreviewData();
        refreshSidebarBadges();
        setSending(false);
        if (queueBehind) {
          warning("Queued. This will run after your other changes finish.", TOAST_MS);
        }
      },
      (err) => {
        setSending(false);
        if (handleBillingError(err, toastError)) return;
        toastError(getApiErrorMessage(err, "Could not queue that change"));
      },
      {
        agent_type: "composite",
        ...(options.model_override !== undefined ? { model_override: options.model_override } : {}),
      },
    );
  };

  return (
    <DashboardLayout title="Preview" currentUser={currentUser} fillHeight>
      <div className="flex min-h-0 flex-1 flex-col-reverse overflow-hidden xl:flex-row-reverse">
        <aside className={`flex min-h-0 w-full shrink-0 flex-col overflow-hidden px-3 py-3 sm:px-5 sm:pl-3 lg:px-6 lg:pl-3 xl:max-h-none xl:w-96 ${draftPanelOpen ? "max-h-[75vh]" : ""}`}>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-theme-secondary bg-theme-main">
            <div className="flex h-[46px] shrink-0 items-center justify-between gap-2 border-b border-theme-secondary bg-theme-secondary px-3 xl:h-[54px]">
              <button
                type="button"
                onClick={() => setDraftPanelOpen((value) => !value)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-semibold xl:pointer-events-none"
              >
                <ChevronDownIcon
                  className={`h-4 w-4 shrink-0 opacity-50 transition-transform xl:hidden ${draftPanelOpen ? "" : "rotate-180"}`}
                  aria-hidden="true"
                />
                <span>
                  Draft changes{" "}
                  <span className="font-medium opacity-50">
                    ({changeRequests.filter((change) => change.status !== "rejected").length})
                  </span>
                </span>
              </button>
              <div className="flex shrink-0 items-center gap-1.5">
                <Link
                  to={sitePath(project, "changes/")}
                  title="History"
                  className="rounded-lg p-2 text-theme-text opacity-60 hover:opacity-100"
                >
                  <ClockIcon className="h-5 w-5" aria-hidden="true" />
                  <span className="sr-only">History</span>
                </Link>
                <button
                  type="button"
                  onClick={publish}
                  disabled={!canPublish || isPublishing}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-theme-primary px-3 py-2 text-xs font-semibold text-theme-button disabled:opacity-40"
                >
                  <RocketLaunchIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  {isPublishing ? "Publishing…" : "Publish"}
                </button>
              </div>
            </div>
            <div className={`grid min-h-0 transition-[grid-template-rows] duration-300 ease-out xl:flex-1 xl:grid-rows-[1fr] ${draftPanelOpen ? "min-h-0 flex-1 grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
              {/* overflow-hidden required so 0fr actually clips (else "Draft opened…" peeks when collapsed) */}
              <div className="min-h-0 overflow-hidden">
                <div ref={chatListRef} className="flex h-full min-h-0 flex-col overflow-y-auto px-3 py-3">
                  {/* mt-auto pins the conversation to the bottom, like a chat. */}
                  <div className="mt-auto space-y-6">
                    {!previewData ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <WeaveLoader size={48} />
                      </div>
                    ) : !chatChanges.length ? (
                      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-theme-secondary px-4 py-8 text-center">
                        <PencilSquareIcon className="h-8 w-8 opacity-40" aria-hidden="true" />
                        <p className="text-sm opacity-60">
                          {siteIsBuilding
                            ? "Your first site build runs here."
                            : "No draft changes. Ask for a copy, data, layout, or section update below."}
                        </p>
                      </div>
                    ) : null}
                    {draftStartedLabel && (
                      <p className="text-center text-xs opacity-40">
                        {`Draft opened ${draftStartedLabel}`}
                      </p>
                    )}
                    {chatChanges.map((change, index) => (
                      <DraftChangeTurn
                        key={change.hash}
                        change={change}
                        isLast={index === chatChanges.length - 1}
                        activeRunHash={runParam}
                        checkpointUrl={checkpointUrl}
                        viewingDraftRoot={viewMode === "draft" && !checkpointUrl}
                        onView={viewCheckpoint}
                        onViewDraft={selectDraft}
                        onOpenLogs={(change) => openChangeLogs(change.hash)}
                        onResolve={resolveChange}
                      />
                    ))}
                    {isPublishing ? (
                      <p className="flex items-center justify-center gap-1.5 text-center text-xs opacity-60">
                        <RocketLaunchIcon className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden="true" />
                        {environment?.status_message || "Publishing to live site"}…
                      </p>
                    ) : canPublish && (
                      <p className="flex items-center justify-center gap-1.5 text-center text-xs opacity-60">
                        <RocketLaunchIcon className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden="true" />
                        <span>
                          {`${unpublishedCompleted.length} draft change${unpublishedCompleted.length === 1 ? "" : "s"}, `}
                          <button type="button" onClick={publish} className="underline hover:opacity-80">
                            publish
                          </button>
                          {" site live"}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid shrink-0 grid-cols-[1fr_auto] gap-2 border-t border-theme-secondary p-3">
              <GrowingTextarea
                value={message}
                onChange={setMessage}
                onSubmit={() => send()}
                disabled={sending}
                placeholder="Describe a change for draft..."
                onExpand={() => {
                  setComposeModelOverride(project?.model_override || "");
                  setComposeOpen(true);
                }}
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={sending || !message.trim()}
                className="flex w-10 items-center justify-center self-stretch rounded-lg bg-theme-primary text-theme-button disabled:opacity-50 xl:w-11"
              >
                →
              </button>
            </div>
          </div>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col px-3 py-0 sm:px-5 sm:py-3 sm:pr-3 lg:px-6 lg:pr-3">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-theme-secondary bg-theme-main">
            {failedSiteBuild && iframeSrc && (
              <div className="flex items-center gap-2 border-b border-theme-secondary px-3 py-2 text-xs">
                <StatusBadge status="failed" />
                <span className="min-w-0 truncate opacity-70">
                  {buildChange?.status_message || "Build failed. Showing the last successful preview."}
                </span>
              </div>
            )}
            <div className="flex h-[54px] shrink-0 flex-wrap items-center gap-2 border-b border-theme-secondary bg-theme-secondary px-3">
              <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden rounded-lg border border-theme-secondary bg-theme-main px-2 py-2 sm:gap-1 sm:px-3">
                {project?.favicon_url ? (
                  <img
                    src={project.favicon_url}
                    alt=""
                    className="hidden h-4 w-4 shrink-0 rounded object-cover sm:block"
                  />
                ) : null}
                <span className="min-w-0 flex-1 truncate whitespace-nowrap text-sm">
                  {displayHost ? (
                    <>
                      <span className={canOpenUrl ? "text-theme-primary" : "text-theme-text opacity-50"}>
                        {displayHost}
                      </span>
                      {showPath && (
                        <span className="text-theme-text opacity-50">{displayPath}</span>
                      )}
                    </>
                  ) : (
                    <span className="text-theme-text opacity-50">No site URL yet</span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={refreshIframe}
                  title="Refresh"
                  disabled={!canOpenUrl}
                  className="shrink-0 rounded p-0.5 text-theme-text opacity-60 hover:opacity-100 disabled:opacity-30 sm:p-1"
                >
                  <ArrowPathIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>
                <button
                  type="button"
                  onClick={copyUrl}
                  title="Copy URL"
                  disabled={!canOpenUrl}
                  className="shrink-0 rounded p-0.5 text-theme-text opacity-60 hover:opacity-100 disabled:opacity-30 sm:p-1"
                >
                  <ClipboardDocumentIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>
                <a
                  href={openHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in new tab"
                  className={`shrink-0 rounded p-0.5 text-theme-text opacity-60 hover:opacity-100 sm:p-1 ${!canOpenUrl ? "pointer-events-none opacity-30" : ""}`}
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </a>
              </div>

              <div className="hidden rounded-lg border border-theme-secondary bg-theme-main md:flex">
                {DEVICE_VIEWS.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    title={label}
                    onClick={() => setDeviceView(id)}
                    className={`px-2.5 py-2 ${deviceView === id ? "bg-theme-primary text-theme-button" : "text-theme-text opacity-60 hover:opacity-100"}`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    <span className="sr-only">{label}</span>
                  </button>
                ))}
              </div>

              {(liveUrl || draftUrl) && (
                <div className="flex rounded-lg border border-theme-secondary bg-theme-main">
                  <button
                    type="button"
                    onClick={selectLive}
                    disabled={!liveUrl}
                    className={`inline-flex items-center gap-1.5 rounded-l-lg px-3 py-2 text-xs font-semibold ${viewMode === "live" ? "bg-theme-primary text-theme-button" : ""} disabled:opacity-40`}
                  >
                    <GlobeAltIcon className="hidden h-4 w-4 sm:block" aria-hidden="true" />
                    Live
                  </button>
                  <button
                    type="button"
                    onClick={selectDraft}
                    className={`inline-flex items-center gap-1.5 rounded-r-lg border-l border-theme-secondary px-3 py-2 text-xs font-semibold ${viewMode === "draft" ? "bg-theme-primary text-theme-button" : ""}`}
                  >
                    <PencilSquareIcon className="hidden h-4 w-4 sm:block" aria-hidden="true" />
                    Draft
                  </button>
                </div>
              )}
            </div>

            <div className="relative min-h-0 flex-1 overflow-auto bg-theme-secondary">
              <div className={`flex h-full min-h-[280px] ${device.width ? "items-start justify-center p-4" : ""}`}>
                <div
                  className={`h-full overflow-hidden bg-theme-main ${device.width ? "rounded-lg border border-theme-secondary shadow-sm" : "w-full"}`}
                  style={device.width ? { width: device.width, maxWidth: "100%", height: "100%" } : undefined}
                >
                  {!previewData ? (
                    <div className="flex h-full items-center justify-center p-8 text-center">
                      <div>
                        <div className="mb-4 flex justify-center">
                          <WeaveLoader size={64} />
                        </div>
                        <p className="font-semibold">Loading preview</p>
                        <p className="mt-2 text-sm opacity-60">
                          Checking draft and live environments…
                        </p>
                      </div>
                    </div>
                  ) : draftComingSoon ? (
                    <div className="flex h-full items-center justify-center p-8 text-center">
                      <div>
                        <div className="mb-4 flex justify-center">
                          <WeaveLoader size={64} />
                        </div>
                        <p className="font-semibold">Your draft preview is coming here soon</p>
                        <p className="mt-2 text-sm opacity-60">
                          The agent is working. This pane will update when the draft is ready.
                        </p>
                      </div>
                    </div>
                  ) : iframeSrc ? (
                    <iframe
                      ref={iframeRef}
                      key={`${iframeTargetKey}:${iframeGen}`}
                      title="Site preview"
                      src={iframeSrc}
                      onLoad={onPreviewIframeLoad}
                      className={`bg-theme-main ${device.width ? "h-full w-full" : "origin-top-left scale-[0.8]"}`}
                      style={device.width ? undefined : { width: "125%", height: "125%" }}
                    />
                  ) : siteIsBuilding ? (
                    <SiteBuildEmptyState
                      project={project}
                      buildChange={buildChange}
                      activeStep={buildStep}
                    />
                  ) : viewMode === "draft" && !hasDraftWork && liveUrl ? (
                    <LiveDraftEmptyState onViewLive={selectLive} />
                  ) : (
                    <SiteBuildEmptyState
                      project={project}
                      buildChange={buildChange}
                      activeStep={buildStep}
                      onViewLive={liveUrl ? selectLive : undefined}
                    />
                  )}
                </div>
              </div>
              {externalBreakout && (
                <ExternalBreakoutModal
                  externalHref={externalBreakout.href}
                  recentPaths={recentPaths}
                  allowStay={externalBreakout.allowStay !== false}
                  onReturn={returnToPreviewPath}
                  onStay={dismissExternalBreakout}
                />
              )}
              {showUpdating && (
                <div
                  className={`pointer-events-none absolute bottom-3 left-3 z-10 rounded-full border border-theme-secondary bg-theme-main/95 px-2.5 py-1 text-[11px] font-medium text-theme-text shadow-sm backdrop-blur-sm transition-opacity duration-500 ${updatingVisible ? "opacity-100" : "opacity-0"}`}
                  aria-live="polite"
                >
                  Updating...
                </div>
              )}
              {draftIsLiveFallback && viewMode === "draft" && iframeSrc && (
                <div className="absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center gap-2 border-t border-theme-secondary bg-theme-main px-3 py-2 text-xs">
                  <span className="min-w-0 flex-1 opacity-70">
                    Your site is live. No draft changes yet; showing the live version.
                  </span>
                  <button
                    type="button"
                    onClick={selectLive}
                    className="shrink-0 rounded-lg bg-theme-primary px-3 py-1.5 font-semibold text-theme-button"
                  >
                    View live
                  </button>
                </div>
              )}
              {previewMissing && viewMode !== "live" && iframeSrc && !hasRunning && (
                <div className="absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center gap-2 border-t border-theme-secondary bg-theme-main px-3 py-2 text-xs">
                  <span className="min-w-0 flex-1 opacity-70">
                    {restoringPreview
                      ? "Restoring this preview. It can take a minute."
                      : "This preview expired. Restore it, or make another change to publish a fresh one."}
                  </span>
                  <button
                    type="button"
                    onClick={restorePreview}
                    disabled={restoringPreview}
                    className="shrink-0 rounded-lg bg-theme-primary px-3 py-1.5 font-semibold text-theme-button disabled:opacity-50"
                  >
                    {restoringPreview ? "Restoring…" : "Restore preview"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {composeOpen && (
        <ComposeChangeModal
          value={message}
          onChange={setMessage}
          modelOverride={composeModelOverride}
          onModelChange={setComposeModelOverride}
          onSubmit={() => {
            setComposeOpen(false);
            send({ model_override: composeModelOverride });
          }}
          onClose={() => setComposeOpen(false)}
          disabled={sending}
        />
      )}
      {logChangeHash && modalChange && (
        <ChangeLogsModal
          change={modalChange}
          showBuildCost={showBuildCost}
          canReject={modalActions.canReject}
          canDiscard={modalActions.canDiscard}
          canRetry={modalActions.canRetry}
          onChangeUpdated={(updated) => {
            setDetailChange(updated);
            patchPreviewChange(updated.hash, updated);
          }}
          onView={(url, runHash) => {
            viewCheckpoint(url, runHash);
            closeChangeLogs();
          }}
          onResolve={resolveChange}
          onDeploymentAction={(deployment, action) => {
            if (action === "rollback") deploymentsService.rollback(project.hash, deployment.hash, loadPreviewData);
            else if (action === "redeploy") {
              deploymentsService.redeploy(project.hash, deployment.hash, (response) => {
                const updated = response?.data;
                if (updated?.hash && logChangeHash) {
                  setDetailChange((current) => (
                    current?.hash === logChangeHash
                      ? { ...current, deployments: [...(current.deployments || []), updated] }
                      : current
                  ));
                  const listItem = changeRequests.find((item) => item.hash === logChangeHash);
                  patchPreviewChange(logChangeHash, {
                    deployments: [...(listItem?.deployments || []), updated],
                  });
                  return;
                }
                loadPreviewData();
              });
            } else if (action === "cancel") deploymentsService.cancel(project.hash, deployment.hash, loadPreviewData);
          }}
          onClose={closeChangeLogs}
        />
      )}
    </DashboardLayout>
  );
};

const DraftChangeTurn = ({
  change,
  isLast,
  activeRunHash,
  checkpointUrl,
  viewingDraftRoot,
  onView,
  onViewDraft,
  onOpenLogs,
  onResolve,
}) => {
  const project = useSelector((state) => state.project.currentProject);
  const runs = change.agent_runs || [];
  const hasActiveRun = change.status !== "canceled"
    && runs.some((run) => run.status === "running" || run.status === "pending");
  const activeRun = hasActiveRun
    ? runs.find((run) => run.status === "running" || run.status === "pending")
    : undefined;
  const isActive = ACTIVE_CHANGE_STATUSES.has(change.status) || hasActiveRun;
  // Same source as Changes modal: prefer the active agent run's status_message.
  const statusLine = activeRun?.status_message || change.status_message || humanizeStatus(change.status);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isActive) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isActive]);

  const { totalSeconds } = changeTotals(change, now);
  const elapsed = totalSeconds >= 60 ? `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s` : `${totalSeconds}s`;
  const buildProgress = change.agent_type === "sitebuilder" && typeof project?.build_progress === "number"
    ? project.build_progress
    : null;
  const previewRun = [...runs].reverse().find((run) => run.preview_url);
  // In-progress (or last with no checkpoint yet): open current draft root, not a past run.
  const viewDraftRoot = isActive || (isLast && !previewRun);
  const canView = isLast || viewDraftRoot || Boolean(previewRun);
  const viewingThis = viewDraftRoot
    ? Boolean(viewingDraftRoot)
    : Boolean(
      previewRun && (
        (activeRunHash && previewRun.hash === activeRunHash)
        || (checkpointUrl && previewUrlPath(checkpointUrl) === previewUrlPath(previewRun.preview_url))
      ),
    );
  const canCancel = change.status === "queued" || change.status === "running" || hasActiveRun;
  const canRetry = isLast && !hasActiveRun && (change.status === "canceled" || change.status === "failed");
  const canUndo = isLast
    && change.agent_type !== "sitebuilder"
    && (change.status === "completed" || change.status === "canceled" || change.status === "failed");
  const startedAt = change.create_date
    ? new Date(change.create_date).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";
  const actionClass = "inline-flex items-center gap-1 opacity-60 hover:underline hover:opacity-100 disabled:opacity-40 disabled:no-underline";

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => onOpenLogs(change)}
          className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl bg-theme-secondary px-3 py-2 text-left text-sm hover:underline"
        >
          {change.description}
          {startedAt && (
            <span className="mt-1 block text-[11px] font-normal opacity-40">{startedAt}</span>
          )}
        </button>
      </div>
      <div className="text-sm">
        {isActive ? (
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-theme-secondary border-t-theme-primary"
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={() => onOpenLogs(change)}
              className="min-w-0 flex-1 text-left opacity-70 hover:underline"
            >
              {statusLine}
            </button>
            {buildProgress != null && buildProgress < 100 ? (
              <span className="shrink-0 text-xs tabular-nums opacity-50">{buildProgress}%</span>
            ) : totalSeconds > 0 ? (
              <span className="shrink-0 text-xs tabular-nums opacity-50">{elapsed}</span>
            ) : null}
          </div>
        ) : (
          <SummaryText
            text={(change.summary || "").trim() || statusLine}
            className="break-words"
          />
        )}
        <div className="mt-2 flex items-center gap-3 text-[11px]">
          <button
            type="button"
            onClick={() => {
              if (viewDraftRoot || !previewRun) {
                onViewDraft();
                return;
              }
              onView(previewRun.preview_url, previewRun.hash);
            }}
            disabled={!canView}
            className={`${actionClass} ${viewingThis ? "text-theme-primary !opacity-100" : ""}`}
          >
            <EyeIcon className="h-3 w-3" aria-hidden="true" />
            View
          </button>
          <button
            type="button"
            onClick={() => onOpenLogs(change)}
            className={actionClass}
          >
            <ListBulletIcon className="h-3 w-3" aria-hidden="true" />
            Detail
          </button>
          {canCancel && (
            <button
              type="button"
              onClick={() => onResolve(change, "cancel")}
              className={actionClass}
            >
              <NoSymbolIcon className="h-3 w-3" aria-hidden="true" />
              Cancel
            </button>
          )}
          {canRetry && (
            <button
              type="button"
              onClick={() => onResolve(change, "retry")}
              className={actionClass}
            >
              <ArrowPathIcon className="h-3 w-3" aria-hidden="true" />
              Retry
            </button>
          )}
          {canUndo && !canCancel && (
            <button
              type="button"
              onClick={() => {
                if (!window.confirm(
                  change.status === "completed"
                    ? "Undo this change? It will be discarded and the draft preview will revert."
                    : "Discard this change? It will be removed and can't be published.",
                )) return;
                onResolve(change, "reject");
              }}
              className={actionClass}
            >
              <ArrowUturnLeftIcon className="h-3 w-3" aria-hidden="true" />
              {change.status === "completed" ? "Undo" : "Discard"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const previewUrlPath = (url) => {
  if (!url) return "";
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

const keepPreviewUrl = (current, next) => {
  if (!next) return current;
  if (!current) return next;
  try {
    const cur = new URL(current);
    const nxt = new URL(next);
    if (cur.pathname !== nxt.pathname) return next;
    if (cur.searchParams.get("sig") === nxt.searchParams.get("sig")) return current;
  } catch {
    if (previewUrlPath(current) === previewUrlPath(next)) return current;
  }
  return next;
};

const draftPreviewRevisionKey = (environment, changeRequests) => {
  const sha = environment?.github_sha || "";
  const previewRuns = (changeRequests || [])
    .filter((change) => ACTIVE_CHANGE_STATUSES.has(change.status))
    .flatMap((change) => change.agent_runs || [])
    .filter((run) => run.status === "succeeded" && run.preview_url)
    .map((run) => run.hash)
    .join(",");
  return `${sha}|${previewRuns}`;
};

const latestRunPreviewUrl = (changeRequests) => {
  for (const change of changeRequests || []) {
    const runs = change.agent_runs || [];
    for (let i = runs.length - 1; i >= 0; i -= 1) {
      if (runs[i].preview_url) return runs[i].preview_url;
    }
  }
  return "";
};

const normalizePreviewPath = (path) => {
  const raw = (path || "/").trim() || "/";
  if (raw.startsWith("/")) return raw;
  return `/${raw}`;
};

const pushRecentPath = (paths, path) => {
  const next = normalizePreviewPath(path);
  const list = Array.isArray(paths) ? paths : [];
  if (list[0] === next) return list;
  return [next, ...list.filter((item) => item !== next)].slice(0, 5);
};

const ExternalBreakoutModal = ({ externalHref, recentPaths, allowStay = true, onReturn, onStay }) => {
  let externalHost = "";
  try {
    externalHost = externalHref ? new URL(externalHref).host : "";
  } catch {
    externalHost = "";
  }
  const primaryPath = recentPaths[0] || "/";
  const otherPaths = recentPaths.slice(1);

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center p-4 text-theme-text">
      {/* Hex theme vars don't support Tailwind /opacity; stack blur + tint instead. */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 backdrop-blur-md" />
        <div className="absolute inset-0 bg-theme-main opacity-55" />
      </div>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="external-breakout-title"
        className="relative w-full max-w-md rounded-2xl border border-theme-secondary bg-theme-main p-5 shadow-lg"
      >
        <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-theme-secondary bg-theme-secondary">
          <ArrowTopRightOnSquareIcon className="h-5 w-5 text-theme-primary" aria-hidden="true" />
        </div>
        <h2 id="external-breakout-title" className="text-center text-lg font-bold text-theme-text">
          You navigated outside of your preview draft
        </h2>
        <p className="mt-2 text-center text-sm text-theme-text opacity-70">
          {!allowStay
            ? "The preview left your draft and opened the Weave app. Go back to keep editing the preview."
            : externalHost
              ? `The preview left your site and opened ${externalHost}.`
              : "The preview left your site."}
        </p>
        <div className="mt-5 space-y-2">
          <button
            type="button"
            onClick={() => onReturn(primaryPath)}
            className="flex w-full flex-col items-center gap-0.5 rounded-lg bg-theme-primary px-4 py-2.5 text-sm font-semibold text-theme-button"
          >
            <span className="flex items-center gap-2">
              <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
              Back to preview
            </span>
            {primaryPath !== "/" && (
              <span className="max-w-full truncate font-normal opacity-80">{primaryPath}</span>
            )}
          </button>
          {otherPaths.length > 0 && (
            <div className="rounded-lg border border-theme-secondary bg-theme-secondary/40 px-3 py-2">
              <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Recent pages
              </p>
              <div className="flex flex-col gap-1">
                {otherPaths.map((path) => (
                  <button
                    key={path}
                    type="button"
                    onClick={() => onReturn(path)}
                    className="truncate rounded px-1.5 py-1 text-left text-sm text-theme-primary hover:bg-theme-secondary"
                  >
                    {path}
                  </button>
                ))}
              </div>
            </div>
          )}
          {allowStay && (
            <button
              type="button"
              onClick={onStay}
              className="w-full rounded-lg border border-theme-secondary bg-theme-secondary/30 px-4 py-2.5 text-sm font-semibold text-theme-text hover:bg-theme-secondary"
            >
              Stay on external site
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/** Same-origin URL on the app host that is not under /draft/ (SPA, not a site preview). */
const isAppShellHref = (href, draftBase) => {
  if (!href || !draftBase || !draftBase.includes("/draft/")) return false;
  try {
    const url = new URL(href);
    const base = new URL(draftBase);
    if (url.origin !== base.origin) return false;
    return !url.pathname.startsWith("/draft/");
  } catch {
    return false;
  }
};

const joinPreviewUrl = (base, pathAndQuery) => {
  if (!base) return "";
  try {
    const baseUrl = new URL(base);
    const url = new URL(base);
    const basePath = url.pathname.replace(/\/$/, "") || "";
    const raw = normalizePreviewPath(pathAndQuery);
    const q = raw.indexOf("?");
    let sitePath = q === -1 ? raw : raw.slice(0, q);
    // PreviewBridge may send the full /draft/... pathname; don't double-prefix.
    if (basePath && (sitePath === basePath || sitePath.startsWith(`${basePath}/`))) {
      sitePath = sitePath.slice(basePath.length) || "/";
    }
    url.pathname = sitePath === "/" ? `${basePath}/` || "/" : `${basePath}${sitePath}`;
    url.search = q === -1 ? "" : raw.slice(q + 1);
    const exp = baseUrl.searchParams.get("exp");
    const sig = baseUrl.searchParams.get("sig");
    if (exp && sig) {
      url.searchParams.set("exp", exp);
      url.searchParams.set("sig", sig);
    }
    url.hash = "";
    return url.toString();
  } catch {
    return base;
  }
};

const previewDisplayHost = (base) => {
  if (!base) return "";
  try {
    return new URL(base).host;
  } catch {
    return String(base).replace(/^https?:\/\//, "").split("/")[0] || "";
  }
};

const ChangeLogsModal = ({
  change,
  showBuildCost = false,
  canReject = false,
  canDiscard = false,
  canRetry = false,
  onView,
  onResolve,
  onDeploymentAction,
  onChangeUpdated,
  onClose,
}) => {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center" onClick={onClose}>
      <div
        className="relative my-auto w-full max-w-3xl overflow-hidden rounded-xl border border-theme-secondary bg-theme-main"
        onClick={(event) => event.stopPropagation()}
      >
        <ChangeCard
          change={change}
          showBuildCost={showBuildCost}
          showPreview={false}
          canReject={canReject}
          canDiscard={canDiscard}
          canRetry={canRetry}
          onViewRun={onView}
          onResolve={onResolve}
          onDeploymentAction={onDeploymentAction}
          onChangeUpdated={onChangeUpdated}
          className="border-0 rounded-none"
        />
      </div>
    </div>,
    document.body,
  );
};

const SiteBuildEmptyState = ({ project, buildChange, activeStep, onViewLive }) => {
  const failed = buildChange?.status === "failed";
  const waitingToStart = !buildChange && project?.status === "draft";
  const isLive = project?.status === "live";

  if (failed) {
    return (
      <div className="flex h-full flex-col items-center justify-center overflow-y-auto p-8 text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/15 text-xl font-bold text-red-500">
          !
        </div>
        <h2 className="text-xl font-bold">Site build failed</h2>
        <p className="mt-2 max-w-sm text-sm opacity-60">
          {buildChange?.status_message || project?.status_message || "Something went wrong during the build."}
        </p>
        <Link
          to={sitePath(project, "changes/")}
          className="mt-6 rounded-lg bg-theme-primary px-6 py-2.5 text-sm font-semibold text-theme-button"
        >
          Review in Changes
        </Link>
      </div>
    );
  }

  if (isLive) {
    return (
      <LiveDraftEmptyState onViewLive={onViewLive} />
    );
  }

  if (waitingToStart) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-theme-secondary bg-theme-secondary text-xl text-theme-primary">
          ◇
        </div>
        <h2 className="text-xl font-bold">Your site preview will live here</h2>
        <p className="mt-2 max-w-sm text-sm opacity-60">
          Generate the site from Changes and this window will show your pages once the first deploy finishes.
        </p>
        <Link
          to={sitePath(project, "changes/")}
          className="mt-6 rounded-lg bg-theme-primary px-6 py-2.5 text-sm font-semibold text-theme-button"
        >
          Open Changes
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center overflow-y-auto p-6 sm:p-10">
      <div className="mb-6">
        <WeaveLoader size={72} />
      </div>
      <h2 className="text-center text-xl font-bold">Building your site</h2>
      <p className="mt-2 max-w-md text-center text-sm opacity-60">
        {buildChange?.status_message || project?.status_message || "Hang tight while we publish your first version."}
      </p>
      <div className="mx-auto mt-8 w-full max-w-md space-y-2">
        {SITE_BUILD_STEPS.map((step, index) => {
          const complete = activeStep > index;
          const current = activeStep === index;
          return (
            <div
              key={step.label}
              className={`flex gap-3 rounded-xl border p-4 transition-all ${
                current ? "border-theme-primary bg-theme-secondary" : "border-theme-secondary"
              } ${index > activeStep ? "opacity-40" : ""}`}
            >
              <div
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                  complete
                    ? "bg-theme-primary text-theme-button"
                    : current
                      ? "animate-pulse border-2 border-theme-primary"
                      : "border-2 border-theme-secondary"
                }`}
              >
                {complete ? "✓" : ""}
              </div>
              <div className="min-w-0 text-left">
                <p className={`text-sm ${current ? "font-semibold" : ""}`}>{step.label}</p>
                <p className="text-xs opacity-50">{step.hint}</p>
              </div>
            </div>
          );
        })}
      </div>
      <Link to={sitePath(project, "changes/")} className="mt-6 text-sm font-semibold text-theme-primary">
        Follow along in Changes
      </Link>
    </div>
  );
};

const LiveDraftEmptyState = ({ onViewLive }) => (
  <div className="flex h-full flex-col items-center justify-center p-8 text-center">
    <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-theme-secondary bg-theme-secondary text-xl text-theme-primary">
      <GlobeAltIcon className="h-7 w-7" aria-hidden="true" />
    </div>
    <h2 className="text-xl font-bold">Your site is live</h2>
    <p className="mt-2 max-w-sm text-sm opacity-60">
      No draft changes right now. Open the live preview, or describe a change to start a new draft.
    </p>
    {onViewLive && (
      <button
        type="button"
        onClick={onViewLive}
        className="mt-6 rounded-lg bg-theme-primary px-6 py-2.5 text-sm font-semibold text-theme-button"
      >
        View live preview
      </button>
    )}
  </div>
);

const humanizeStatus = (status) => {
  if (status === "completed") return "Done";
  const text = (status || "").replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const GrowingTextarea = ({ value, onChange, onSubmit, disabled, placeholder, onExpand }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // Mobile: icons sit in a row → one-line height. xl: stacked expand/mic → taller min.
    const min = window.matchMedia("(min-width: 1280px)").matches ? 72 : 40;
    el.style.height = `${Math.max(min, Math.min(el.scrollHeight, 200))}px`;
  }, [value]);

  return (
    <div className="relative min-w-0">
      <textarea
        ref={ref}
        rows={1}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            onSubmit();
          }
        }}
        className="box-border block max-h-52 min-h-[40px] w-full resize-none overflow-y-auto rounded-lg border border-theme-shadow py-2 pl-3 pr-[4.25rem] text-sm leading-5 disabled:opacity-60 xl:min-h-[72px] xl:py-2.5 xl:pr-9"
      />
      <div className="absolute bottom-1.5 right-1.5 top-1.5 flex items-center gap-0.5 xl:flex-col xl:items-end xl:justify-between">
        {onExpand && (
          <button
            type="button"
            onClick={onExpand}
            disabled={disabled}
            title="Write a longer change"
            className="rounded p-1 text-theme-text opacity-50 hover:opacity-100 disabled:opacity-30"
          >
            <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Expand</span>
          </button>
        )}
        <VoiceInputButton
          value={value}
          onChange={onChange}
          disabled={disabled}
          className="rounded p-1 text-theme-text opacity-50 hover:opacity-100 disabled:opacity-30"
        />
      </div>
    </div>
  );
};

const ComposeChangeModal = ({ value, onChange, modelOverride, onModelChange, onSubmit, onClose, disabled }) => {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center" onClick={onClose}>
      <div
        className="relative my-auto w-full max-w-lg rounded-xl border border-theme-secondary bg-theme-main"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-theme-secondary px-4 py-3">
          <h2 className="text-sm font-semibold">Describe a change</h2>
          <button type="button" onClick={onClose} className="rounded p-1 opacity-60 hover:opacity-100">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-4">
          <div className="relative">
            <textarea
              autoFocus
              rows={8}
              value={value}
              disabled={disabled}
              placeholder="What should we change on draft? Layout, copy, sections, SEO…"
              onChange={(event) => onChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  if (value.trim()) onSubmit();
                }
              }}
              className="min-h-[180px] w-full resize-y rounded-lg border border-theme-secondary px-3 py-2 pr-10 text-sm leading-6 disabled:opacity-60"
            />
            <VoiceInputButton
              value={value}
              onChange={onChange}
              disabled={disabled}
              className="absolute right-2 top-2 rounded p-1 text-theme-text opacity-50 hover:opacity-100 disabled:opacity-30"
            />
          </div>
          <div>
            <p className="mb-2 text-xs font-medium opacity-70">Model for this change</p>
            <PremiumModelPicker value={modelOverride || ""} onChange={onModelChange} />
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] opacity-50">⌘/Ctrl + Enter to send</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-theme-secondary px-3 py-2 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSubmit}
                disabled={disabled || !value.trim()}
                className="rounded-lg bg-theme-primary px-3 py-2 text-xs font-semibold text-theme-button disabled:opacity-50"
              >
                Queue change
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Preview;
